import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { UntypedFormGroup } from '@angular/forms';
import { Router } from '@angular/router';
import { SnackbarService } from '@vendasta/galaxy/snackbar-service';
import { Observable } from 'rxjs';
import { filter, map, take, tap } from 'rxjs/operators';

import { TimeRangeOption } from '@vendasta/forms';
import {
  ButtonAction,
  ButtonOptions,
  CatalogueData,
  ControlType,
  FormOptions,
  FormResult,
  Section,
} from './vform.models';
import { FormState, NotificationType, VFormStoreService } from './vform.store.service';

@Component({
  // TODO rename this to have the prefix vform
  // eslint-disable-next-line @angular-eslint/component-selector
  selector: 'v-form',
  templateUrl: 'vform.component.html',
  styleUrls: ['vform.component.scss'],
  providers: [VFormStoreService],
})
export class VFormComponent implements OnInit {
  @Input() formOptions: FormOptions;
  @Input() showDefaultSnackbars = true;
  @Input() inputWidth = '50%';
  @Input() fieldWidth = '500px';
  @Input() compact = false;

  // withCredentials passes in the authorization header if it is set to true, current behavior is always false so this is the default value
  @Input() withCredentials = false;

  @Output() formSubmitSuccess: EventEmitter<boolean> = new EventEmitter();
  @Output() formSubmitResult: EventEmitter<FormResult> = new EventEmitter();
  @Output() hasError: EventEmitter<boolean> = new EventEmitter();
  @Output() errorMessage: EventEmitter<string> = new EventEmitter();
  @Output() formLoadSuccess: EventEmitter<FormState> = new EventEmitter();
  @Output() formSubmitFn: EventEmitter<(...args: any[]) => any> = new EventEmitter();

  vFormStore$: Observable<FormState>;
  sections$: Observable<Section[]>;
  catalogue$: Observable<CatalogueData>;
  formGroup$: Observable<UntypedFormGroup>;
  leftButtons: ButtonOptions[];
  rightButtons: ButtonOptions[];
  public formSubmit: (...args: any[]) => any;
  controlType = ControlType;

  today = new Date();
  timeRangeOption = '';
  timeRangeOptions: TimeRangeOption[] = [
    { key: '', name: 'Select a date range' },
    { key: '1days', name: 'Today' },
    { key: '7days', name: 'Last 7 days' },
    { key: '30days', name: 'Last 30 days' },
    { key: '90days', name: 'Last 90 days' },
    { key: '6months', name: 'Last 6 months' },
    { key: '12months', name: 'Last 12 months' },
    { key: 'custom', name: 'Custom' },
  ];

  private dateRange: Date[] = [new Date(), new Date()];
  private timeRange = '';

  constructor(private vFormStore: VFormStoreService, private alertService: SnackbarService, private router: Router) {}

  ngOnInit() {
    if (!this.formOptions.context) {
      this.formOptions.context = document.URL;
    }
    this.vFormStore.initStore(this.formOptions);
    this.vFormStore$ = this.vFormStore.formState$.pipe(tap((store) => this.formLoadSuccess.emit(store)));
    this.vFormStore.initVForm(this.withCredentials)(this.formOptions);
    this.formSubmit = this.vFormStore.formSubmit(this.withCredentials);
    this.vFormStore.notification$.pipe(filter((n) => n !== null)).subscribe((notification) => {
      if (notification.type === NotificationType.SUCCESS) {
        if (this.showDefaultSnackbars) {
          this.alertService.openSuccessSnack(notification.message);
        }
      } else {
        if (this.showDefaultSnackbars) {
          this.alertService.openErrorSnack(notification.message);
        }
        this.hasError.emit(true);
        this.errorMessage.emit(notification.message);
      }
    });
    this.formGroup$ = this.vFormStore$.pipe(map((store) => store.formGroup));
    this.formSubmitFn.emit(this.submitData.bind(this));
    this.sections$ = this.vFormStore$.pipe(map((store) => store.sections));
    this.catalogue$ = this.vFormStore$.pipe(map((store) => store.catalogue));
    this.rightButtons =
      this.formOptions && this.formOptions.rightCustomButtons ? this.formOptions.rightCustomButtons : [];
    this.leftButtons = this.computeLeftButtons();
  }

  public computeLeftButtons(): ButtonOptions[] {
    if (this.formOptions && this.formOptions.leftCustomButtons) {
      return this.formOptions.leftCustomButtons;
    }
    return [
      {
        label: 'Submit',
        action: ButtonAction.SUBMIT,
        color: 'primary',
      },
      {
        label: 'Cancel',
        action: ButtonAction.CANCEL,
      },
    ];
  }

  submitData(): void {
    this.formGroup$.pipe(take(1)).subscribe({
      next: (formGroup) => {
        const formData = formGroup.getRawValue();
        const formSubmission = {
          ...formData,
        };
        this.formSubmit(formSubmission, this.formSubmitSuccess, this.formSubmitResult);
      },
    });
  }

  callButtonAction(action: (formdata?: any) => void | ButtonAction, formdata: any): void {
    if (typeof action === 'number') {
      switch (action) {
        case ButtonAction.SUBMIT:
          this.formSubmit(formdata, this.formSubmitSuccess, this.formSubmitResult);
          break;
        case ButtonAction.CLEAR:
          this.vFormStore.clearAll()();
          break;
        case ButtonAction.CANCEL:
          this.router.navigateByUrl(this.formOptions.cancelUrl);
          break;
      }
    } else {
      action.call(this, formdata);
    }
  }

  onDateRangeChange(dateRange: Date[]): void {
    this.dateRange = dateRange;

    this.formGroup$.pipe(take(1)).subscribe({
      next: (formGroup: UntypedFormGroup) => {
        formGroup.patchValue({
          date_range_picker: JSON.stringify(this.getDateRange()),
        });
      },
    });
  }

  onTimeRangeChange(rangeName: string): void {
    this.timeRange = rangeName;
  }

  getDateRange(): any | undefined {
    if (!this.timeRange || !this.dateRange) {
      return undefined;
    }

    function formatDate(date: Date) {
      return date.toISOString().split('T')[0];
    }

    return {
      start: formatDate(this.dateRange[0]),
      end: formatDate(this.dateRange[1]),
    };
  }

  shouldIndentControlHint(control: ControlType): boolean {
    return [ControlType.CHECKBOX, ControlType.RADIO].indexOf(control) > -1;
  }
}
